import log from 'loglevel';

import {
  FAVICON_BADGE_CANVAS_SIZE,
  FAVICON_BADGE_DOT_COLOR,
  FAVICON_BADGE_DOT_RADIUS_RATIO,
  FAVICON_BADGE_IMAGE_TYPE,
  FAVICON_BADGE_LOGO_INSET_RATIO,
  FAVICON_BADGE_RING_COLOR,
  FAVICON_BADGE_RING_WIDTH_RATIO,
  FAVICON_LINK_REL,
  FAVICON_PATH,
} from '../../utils/constants/suggestionTabAlerts';

const CANVAS_CONTEXT_TYPE = '2d';
const LINK_SELECTOR = `link[rel="${FAVICON_LINK_REL}"]`;
const FULL_CIRCLE = Math.PI * 2;

let badgedDataUrlPromise: Promise<string | null> | null = null;

export function ensureFaviconLink(): HTMLLinkElement {
  const existing = document.head.querySelector<HTMLLinkElement>(LINK_SELECTOR);
  if (existing) return existing;
  const link = document.createElement('link');
  link.rel = FAVICON_LINK_REL;
  link.href = FAVICON_PATH;
  document.head.appendChild(link);
  return link;
}

function loadFaviconImage(): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('favicon image failed to load'));
    image.src = FAVICON_PATH;
  });
}

function drawCircle(
  context: CanvasRenderingContext2D,
  center: { x: number; y: number },
  radius: number,
  color: string,
): void {
  context.beginPath();
  context.arc(center.x, center.y, radius, 0, FULL_CIRCLE);
  context.fillStyle = color;
  context.fill();
}

function drawLogo(
  context: CanvasRenderingContext2D,
  image: HTMLImageElement,
  size: number,
): void {
  const inset = size * FAVICON_BADGE_LOGO_INSET_RATIO;
  context.drawImage(image, 0, inset, size - inset, size - inset);
}

function drawBadge(context: CanvasRenderingContext2D, size: number): void {
  const inset = size * FAVICON_BADGE_LOGO_INSET_RATIO;
  const dotRadius = size * FAVICON_BADGE_DOT_RADIUS_RATIO;
  const ringRadius = dotRadius + size * FAVICON_BADGE_RING_WIDTH_RATIO;
  const center = { x: size - inset, y: inset };
  drawCircle(context, center, ringRadius, FAVICON_BADGE_RING_COLOR);
  drawCircle(context, center, dotRadius, FAVICON_BADGE_DOT_COLOR);
}

async function renderBadgedFavicon(): Promise<string | null> {
  try {
    const image = await loadFaviconImage();
    const canvas = document.createElement('canvas');
    canvas.width = FAVICON_BADGE_CANVAS_SIZE;
    canvas.height = FAVICON_BADGE_CANVAS_SIZE;
    const context = canvas.getContext(CANVAS_CONTEXT_TYPE);
    if (!context) throw new Error('2d canvas context unavailable');
    drawLogo(context, image, FAVICON_BADGE_CANVAS_SIZE);
    drawBadge(context, FAVICON_BADGE_CANVAS_SIZE);
    return canvas.toDataURL(FAVICON_BADGE_IMAGE_TYPE);
  } catch (err) {
    log.warn('buildBadgedFaviconDataUrl', err);
    return null;
  }
}

export function buildBadgedFaviconDataUrl(): Promise<string | null> {
  badgedDataUrlPromise ??= renderBadgedFavicon();
  return badgedDataUrlPromise;
}
